/**
 * Named hooks over the app's singleton stores - one per observer, so a
 * component says WHAT it watches and the adapter mechanics stay in one file.
 */

import { auth } from '../../auth/Auth'
import { missionState } from '../../learn/missionState'
import { currentLighting, onLightingChange, type LightingId } from '../../fab/lighting'
import { currentHall, onHallChange, type HallLayout } from '../../fab/hall'
import { onPlansChange } from '../../fab/floorPlan'
import { currentQuality, onQualityChange, type QualityChoice } from '../../fab/quality'
import { onSoundChange, soundMuted } from '../../fab/sound'
import { onInputModeChange, touchInput } from '../../ui/inputMode'
import { panelStore } from '../panel/panelStore'
import { onEnteredChange, walkEntered } from '../input/walkLock'
import type { Tool } from '../../fab/layout'
import { useObservable, useTick } from './useObservable'

/** Re-render on any auth movement; read `auth.*` directly after calling. */
export function useAuthTick(): number {
  return useTick((fn) => auth.subscribe(fn))
}

/** Re-render on mission progress; read `missionState.*` directly. */
export function useMissionTick(): number {
  return useTick((fn) => missionState.subscribe(fn))
}

export function useLighting(): LightingId {
  return useObservable(onLightingChange, currentLighting)
}

export function useHall(): HallLayout {
  return useObservable(onHallChange, currentHall)
}

/** Re-render when a floor plan is saved, deleted or made active. */
export function usePlans(): number {
  return useTick(onPlansChange)
}

export function useQuality(): QualityChoice {
  return useObservable(onQualityChange, currentQuality)
}

/** Whether the hall's sound is muted. */
export function useSoundMuted(): boolean {
  return useObservable(onSoundChange, soundMuted)
}

/** The tool whose panel is open, or null while the student has the floor. */
export function useOpenTool(): Tool | null {
  return useObservable(panelStore.subscribe, () => panelStore.open)
}

/** Whether the student has crossed the airlock yet. */
export function useEntered(): boolean {
  return useObservable(onEnteredChange, walkEntered)
}

/** True when the last press was a finger - surfaces word themselves by it. */
export function useInputMode(): boolean {
  return useObservable(onInputModeChange, touchInput)
}
